import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import { Home, Search, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface DebitVoucherCheck {
  id: string;
  voucher_number: string;
  voucher_date: string;
  voucher_head: string;
  amount: number;
  payment_method: string;
  reference_number?: string;
  description?: string;
  created_by: string;
  status: string;
  checked_by?: string;
  checked_at?: string;
}

export default function DebitVCheck() {
  const { formatCurrency } = useCurrency();
  const [vouchers, setVouchers] = useState<DebitVoucherCheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('pending');

  useEffect(() => {
    loadPendingVouchers();
  }, [statusFilter]);

  const loadPendingVouchers = async () => {
    try {
      setLoading(true);
      
      const url = import.meta.env.VITE_SUPABASE_URL;
      if (!url || url === 'https://placeholder.supabase.co') {
        setVouchers(getMockPendingVouchers());
        setLoading(false);
        return;
      }

      let query = supabase
        .from('vouchers')
        .select('*')
        .eq('voucher_type', 'debit');

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query.order('voucher_date', { ascending: false });
      if (error) throw error;
      setVouchers(data || []);
    } catch (error) {
      console.error('Error loading vouchers:', error);
      setVouchers(getMockPendingVouchers());
    } finally {
      setLoading(false);
    }
  };

  const getMockPendingVouchers = (): DebitVoucherCheck[] => {
    return [
      {
        id: '1',
        voucher_number: 'DV-2025-003',
        voucher_date: '2025-01-27',
        voucher_head: 'Medical Supplies',
        amount: 50000,
        payment_method: 'cash',
        reference_number: 'REF-003',
        description: 'Purchase of medical supplies',
        created_by: 'Admin',
        status: 'pending',
      },
      {
        id: '2',
        voucher_number: 'DV-2025-004',
        voucher_date: '2025-01-26',
        voucher_head: 'Equipment Maintenance',
        amount: 25000,
        payment_method: 'bank_transfer',
        reference_number: 'REF-004',
        description: 'Equipment maintenance and repair',
        created_by: 'Admin',
        status: 'pending',
      },
    ];
  };

  const filteredVouchers = vouchers.filter(voucher => {
    const matchesSearch = 
      voucher.voucher_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      voucher.voucher_head.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const handleApprove = async (id: string) => {
    if (!confirm('Are you sure you want to approve this voucher?')) return;

    try {
      const url = import.meta.env.VITE_SUPABASE_URL;
      if (!url || url === 'https://placeholder.supabase.co') {
        alert('Voucher approved successfully (mock mode)');
        await loadPendingVouchers();
        return;
      }

      const { error } = await supabase
        .from('vouchers')
        .update({
          status: 'approved',
          checked_by: 'Current User',
          checked_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;
      await loadPendingVouchers();
    } catch (error) {
      console.error('Error approving voucher:', error);
      alert('Error approving voucher');
    }
  };

  const handleReject = async (id: string) => {
    if (!confirm('Are you sure you want to reject this voucher?')) return;

    try {
      const url = import.meta.env.VITE_SUPABASE_URL;
      if (!url || url === 'https://placeholder.supabase.co') {
        alert('Voucher rejected successfully (mock mode)');
        await loadPendingVouchers();
        return;
      }

      const { error } = await supabase
        .from('vouchers')
        .update({
          status: 'rejected',
          checked_by: 'Current User',
          checked_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;
      await loadPendingVouchers();
    } catch (error) {
      console.error('Error rejecting voucher:', error);
      alert('Error rejecting voucher');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Home className="w-4 h-4" />
            <span>/</span>
            <span>Billings/Reception</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">Debit V. Check</span>
          </div>
        </div>
      </div>

      <div className="max-w-none px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Pending Vouchers</p>
            <p className="text-2xl font-bold text-yellow-600 mt-1">
              {filteredVouchers.filter(v => v.status === 'pending').length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Approved Today</p>
            <p className="text-2xl font-bold text-green-600 mt-1">
              {filteredVouchers.filter(v => v.status === 'approved').length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Rejected Today</p>
            <p className="text-2xl font-bold text-red-600 mt-1">
              {filteredVouchers.filter(v => v.status === 'rejected').length}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search vouchers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="all">All</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Voucher #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Head
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : filteredVouchers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                      No vouchers found
                    </td>
                  </tr>
                ) : (
                  filteredVouchers.map((voucher) => (
                    <tr key={voucher.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {voucher.voucher_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(voucher.voucher_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {voucher.voucher_head}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-bold">
                        {formatCurrency(voucher.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          voucher.status === 'approved' ? 'bg-green-100 text-green-800' :
                          voucher.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {voucher.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {voucher.status === 'pending' && (
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleApprove(voucher.id)}
                              className="flex items-center space-x-1 text-green-600 hover:text-green-800"
                            >
                              <CheckCircle className="w-4 h-4" />
                              <span>Approve</span>
                            </button>
                            <button
                              onClick={() => handleReject(voucher.id)}
                              className="flex items-center space-x-1 text-red-600 hover:text-red-800"
                            >
                              <XCircle className="w-4 h-4" />
                              <span>Reject</span>
                            </button>
                          </div>
                        )}
                        {voucher.status !== 'pending' && (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
